import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getPortalDocuments, type PortalDocument } from '@lib/api/portal';
import { PullToRefresh } from '@/components/common';
import { DocumentViewerModal } from '@/components/common/DocumentViewerModal';

const DOC_CONFIG: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  invoice:        { icon: 'receipt_long',  color: 'text-blue-600',   bg: 'bg-blue-50',   label: 'Invoice' },
  quote:          { icon: 'request_quote', color: 'text-purple-600', bg: 'bg-purple-50', label: 'Quote' },
  work_order:     { icon: 'handshake',     color: 'text-amber-600',  bg: 'bg-amber-50',  label: 'Work Order' },
  work_proposal:  { icon: 'handshake',     color: 'text-amber-600',  bg: 'bg-amber-50',  label: 'Work Proposal' },
  sub_agreement:  { icon: 'gavel',         color: 'text-amber-600',  bg: 'bg-amber-50',  label: 'Sub Agreement' },
  contract:       { icon: 'gavel',         color: 'text-gray-600',   bg: 'bg-gray-100',  label: 'Contract' },
  scope_of_work:  { icon: 'checklist',     color: 'text-gray-600',   bg: 'bg-gray-100',  label: 'Scope of Work' },
  change_order:   { icon: 'change_circle', color: 'text-orange-600', bg: 'bg-orange-50', label: 'Change Order' },
  photo:          { icon: 'image',         color: 'text-sky-600',    bg: 'bg-sky-50',    label: 'Photo' },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  sent:   { label: 'Sent',   color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signed', color: 'bg-green-100 text-green-700' },
  issued: { label: 'Issued', color: 'bg-sky-100 text-sky-700' },
  void:   { label: 'Void',   color: 'bg-gray-100 text-gray-500' },
};

// CR #157 — sub-groups inside each project folder, built from what the data
// really is: uploaded photos, agreements to sign, written contracts/scopes,
// and everything else the office has shared. Invoices live on their own tab.
type DocKind = 'photos' | 'agreements' | 'contracts' | 'files';
const KIND_META: Record<DocKind, { label: string; icon: string }> = {
  photos:     { label: 'Photos',            icon: 'photo_library' },
  agreements: { label: 'Agreements',        icon: 'draw' },
  contracts:  { label: 'Contracts & scopes', icon: 'gavel' },
  files:      { label: 'Files we\'ve shared', icon: 'attach_file' },
};
const KIND_ORDER: DocKind[] = ['agreements', 'contracts', 'photos', 'files'];

type Doc = PortalDocument & { _source?: string; sign_token?: string };

const kindOf = (d: Doc): DocKind => {
  if (d._source === 'agreement') return 'agreements';
  if (d._source === 'project_doc') return 'contracts';
  return Number(d.is_photo) === 1 ? 'photos' : 'files';
};

// Handles both bare DATE strings and ISO datetimes — appending T12:00:00 to a
// full timestamp produced "Invalid Date" on every uploaded file (audit)
const fmtDate = (d: string) => {
  const date = String(d).includes('T') ? new Date(d) : new Date(d + 'T12:00:00');
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const ClientDocumentsPage = () => {
  const [docs, setDocs] = useState<Doc[]>([]);
  // CR #137 — per-job folders start expanded; this tracks the ones you close
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // CR #155 — in-app viewer (same one the employee portal and office use)
  const [viewerDoc, setViewerDoc] = useState<{ id: number; name: string; type: string; url: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getPortalDocuments();
      setDocs(data as Doc[]);
      setError(null);
    } catch {
      setError('Failed to load documents. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // #16 – download a shared file (kept on the scoped endpoint)
  const [downloadError, setDownloadError] = useState('');
  const downloadById = (docId: number, name: string) => {
    const token = localStorage.getItem('accessToken');
    setDownloadError('');
    fetch(`/api/portal/me/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('unavailable');
        return res.blob();
      })
      .then(blob => {
        const burl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = burl;
        a.download = name;
        a.click();
        URL.revokeObjectURL(burl);
      })
      .catch(() => setDownloadError('That file isn\'t downloadable right now — call us and we\'ll send it over.'));
  };

  // CR #155 — tap a shared file to see it right here (photos, PDFs…)
  const openViewer = (doc: Doc) => {
    if (!doc.file_path) return;
    setViewerDoc({
      id: doc.id,
      name: doc.title || doc.file_name || 'Document',
      type: doc.file_type || '',
      url: `/${String(doc.file_path).replace(/^\//, '')}`,
    });
  };

  // Hooks must run on every render — compute the grouping BEFORE any early
  // return, otherwise the loading/error paths render fewer hooks (React #310).
  const groupedByJob = useMemo(() => {
    // job → kind → docs (CR #137 folders, CR #157 sub-groups)
    const map = new Map<string, Map<DocKind, Doc[]>>();
    for (const d of docs) {
      const key = d.job_title || 'General';
      if (!map.has(key)) map.set(key, new Map());
      const kinds = map.get(key)!;
      const k = kindOf(d);
      if (!kinds.has(k)) kinds.set(k, []);
      kinds.get(k)!.push(d);
    }
    return [...map.entries()].sort((a, b) =>
      a[0] === 'General' ? 1 : b[0] === 'General' ? -1 : a[0].localeCompare(b[0]));
  }, [docs]);

  if (loading) return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
    </div>
  );
  if (error) return <div className="px-4 py-6 text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200"><span className="material-symbols-outlined text-base">error</span>{error}</div>;

  const renderCard = (doc: Doc) => {
    const type = doc.document_type || '';
    const cfg = Number(doc.is_photo) === 1
      ? DOC_CONFIG.photo
      : DOC_CONFIG[type] ?? { icon: 'description', color: 'text-gray-500', bg: 'bg-gray-100', label: type.replace(/_/g, ' ') || 'Document' };
    const status = doc.status && doc.status !== 'shared' ? STATUS_CONFIG[doc.status] : null;
    const isAgreement = doc._source === 'agreement';
    const viewable = !!doc.file_path;

    const body = (
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
          <span className={`material-symbols-outlined text-xl ${cfg.color}`}>{cfg.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {doc.title || doc.file_name || 'Document'}
            </p>
            <div className="flex items-center gap-1 flex-shrink-0">
              {status && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
                  {status.label}
                </span>
              )}
              {/* CR #155 — files open in the viewer with one tap */}
              {viewable && (
                <span className="material-symbols-outlined text-lg text-gray-300 ml-1">visibility</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 mt-1.5">
            <span className={`text-xs font-medium ${cfg.color} flex items-center gap-1`}>
              <span className="material-symbols-outlined text-xs">{cfg.icon}</span>
              {cfg.label}
            </span>
            {doc.created_at && (
              <span className="text-xs text-gray-400">{fmtDate(doc.created_at)}</span>
            )}
          </div>
          {/* Agreements awaiting signature get a real action (audit:
              clients could see a sent contract but not sign it) */}
          {isAgreement && doc.status === 'sent' && doc.sign_token && (
            <a href={`/agreements/sign/${doc.sign_token}`}
              onClick={e => e.stopPropagation()}
              className="mt-2.5 flex items-center justify-center gap-1.5 w-full py-2 bg-[#C77D3F] text-white rounded-lg text-sm font-bold active:scale-[0.99]">
              <span className="material-symbols-outlined text-base">draw</span>
              Review &amp; sign
            </a>
          )}
        </div>
      </div>
    );

    return viewable ? (
      <button key={`${doc._source}-${doc.id}`} type="button" onClick={() => openViewer(doc)}
        className="w-full text-left bg-white rounded-xl border border-gray-200 p-4 active:bg-[#FAF6F0]">
        {body}
      </button>
    ) : (
      <div key={`${doc._source}-${doc.id}`} className="bg-white rounded-xl border border-gray-200 p-4">
        {body}
      </div>
    );
  };

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-[#2C1810]">Documents</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your project agreements, contracts, photos and files — tap any file to open it.</p>
        </div>

        {downloadError && (
          <p className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{downloadError}</p>
        )}

        {docs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">folder_open</span>
            <p className="text-sm font-medium text-gray-500 mt-3">No documents yet</p>
            <p className="text-xs text-gray-400 mt-1">Your agreements and project files will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* CR #137 folders + CR #157 sub-groups, all expanded by default */}
            {groupedByJob.map(([jobName, kinds]) => {
              const total = [...kinds.values()].reduce((n, arr) => n + arr.length, 0);
              return (
                <div key={jobName} className="space-y-2">
                  <button
                    onClick={() => setCollapsed(c => ({ ...c, [jobName]: !c[jobName] }))}
                    className="w-full flex items-center gap-2 px-1 py-2 text-left"
                  >
                    <span className={`material-symbols-outlined text-lg text-[#C77D3F] transition-transform ${collapsed[jobName] ? '' : 'rotate-90'}`}>
                      chevron_right
                    </span>
                    <span className="material-symbols-outlined text-lg text-[#C77D3F]">
                      {collapsed[jobName] ? 'folder' : 'folder_open'}
                    </span>
                    <span className="text-base font-black text-[#2C1810] flex-1 truncate">{jobName}</span>
                    <span className="text-xs text-gray-400">{total} file{total === 1 ? '' : 's'}</span>
                  </button>
                  {!collapsed[jobName] && (
                    <div className="space-y-3 pl-2">
                      {KIND_ORDER.filter(k => kinds.has(k)).map(k => (
                        <div key={k} className="space-y-2">
                          <p className="flex items-center gap-1.5 px-1 text-xs font-bold uppercase tracking-wide text-gray-400">
                            <span className="material-symbols-outlined text-sm text-[#C77D3F]">{KIND_META[k].icon}</span>
                            {KIND_META[k].label}
                            <span className="font-medium normal-case tracking-normal">· {kinds.get(k)!.length}</span>
                          </p>
                          {kinds.get(k)!.map(renderCard)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Invoices deliberately aren't duplicated here (#157) */}
            <Link to="/portal/client/invoices"
              className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500 active:bg-[#FAF6F0]">
              <span className="material-symbols-outlined text-lg text-[#C77D3F]">receipt_long</span>
              <span className="flex-1">Looking for an invoice? Those live in the Invoices tab.</span>
              <span className="material-symbols-outlined text-gray-300">chevron_right</span>
            </Link>
          </div>
        )}
      </div>
      <DocumentViewerModal
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
        onDownload={d => downloadById(d.id, d.name)}
      />
    </PullToRefresh>
  );
};
